import React, { useContext, useMemo } from 'react';
import { modeUIContext, selectSlideOutPanelOptions } from '@moderepo/mode-ui-state';
import { SlideOutPanel, SlideOutCompProps } from '@moderepo/mode-app-components';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';



export const SlideOutPanelProvider: React.FC = () => {
    const { state:uiState } = useContext(modeUIContext);
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);

    /**
     * Get the SlideOutPanel component based on the slideOutPanelOptions set in the uiState.
     * Because the SlideOutPanel provider is generic, we don't know which SlideOutPanel to create based on the options. Therefore, we
     * will call the project handler to handle it. The project handler knows which SlideOutPanel to create based on the type set in the
     * slideOutPanelOptions.
     */
    const SlideOutPanelComp = useMemo((): React.ReactElement<SlideOutCompProps> | undefined => {
        // Get the handler for the specified entity type. If a handler is returned, call the handler's getPreviewComponent to get the
        // preview component for the specified entity type
        if (slideOutPanelOptions) {
            const handler = ProjectTypeHandlersManager.getInstance().getSlideOutPanelComponentHandler(slideOutPanelOptions.compType);
            const slideOutComp = handler ? handler.getSlideOutComponent(slideOutPanelOptions) : undefined;

            // Many times I implemented a new Preview component type but forgot to register the component therefore the panel shows blank
            // content and I have to debug to figure out why. Displaying this error in the console will help save me time debugging.
            if (!slideOutComp) {
                // eslint-disable-next-line no-console
                console.error(
                    `Unknown preview panel type '${slideOutPanelOptions.compType}'. Make sure it is registered with the ProjectTypeHandlersManager`,
                );
            }

            return slideOutComp;
        }

        return undefined;
    }, [slideOutPanelOptions]);


    return (
        <SlideOutPanel>
            { SlideOutPanelComp }
        </SlideOutPanel>
    );
};
